import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { PortalSidebar } from "@/components/portal/PortalSidebar";
import { usePortalAuth } from "@/lib/contexts/PortalAuthContext";
import { usePartnerAccessTier } from "@/lib/hooks/usePartnerAccessTier";
import { supabase } from "@/src/integrations/supabase/client";
import portalPhotoLeft from "@/assets/portal-photo-left.jpg";
import portalPhotoRight from "@/assets/portal-photo-right.jpg";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/theme-toggle";

interface PortalLayoutProps {
  children: ReactNode;
}

/**
 * Shared chrome for every portal page: branded sidebar + sticky header.
 * Mirrors the Craft Commerce Hub PortalLayout (pixel-for-pixel where possible),
 * but adapted to this project's auth/tier hooks.
 */
export default function PortalLayout({ children }: PortalLayoutProps) {
  const { user, loading: authLoading, signOut } = usePortalAuth();
  const { tier, partnerId, loading: tierLoading } = usePartnerAccessTier();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setIsStaff(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (cancelled) return;
      const roles = (data ?? []).map((r: any) => r.role);
      setIsAdmin(roles.includes("admin"));
      setIsStaff(roles.some((r: string) => ["admin", "editor", "viewer"].includes(r)));
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/portal/login");
  };

  if (authLoading || tierLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#ffffff" }}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
          <p className="text-sm text-muted-foreground">Loading portal…</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Temp-tier (non-admin) users have no sidebar and are locked to the temp home page.
  const hideSidebar = tier === "temp" && !isStaff;

  return (
    <SidebarProvider style={{ ['--sidebar-width-icon' as any]: '4.5rem' }}>
      <div className="min-h-screen flex w-full" style={{ background: "#ffffff" }}>
        {!hideSidebar && (
          <PortalSidebar tier={tier} isAdmin={isStaff} onSignOut={handleSignOut} />
        )}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b bg-background/80 backdrop-blur-sm px-4 sticky top-0 z-30">
            <div className="flex items-center">
              {!hideSidebar && <SidebarTrigger className="mr-4" />}
              <div className="flex items-center gap-2">
                <div
                  className="w-1.5 h-6 rounded-full"
                  style={{ background: "var(--primary)" }}
                />
                <h1 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>
                  Beverage Federation
                </h1>
              </div>
              {isAdmin && (
                <span
                  className="ml-3 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
                  style={{
                    background: "rgba(247,168,0,0.2)",
                    color: "#b07a00",
                  }}
                >
                  Admin
                </span>
              )}
              {tier === "temp" && partnerId && (
                <span
                  className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
                  style={{
                    background: "rgba(59,130,246,0.15)",
                    color: "#1d4ed8",
                  }}
                >
                  Temp Access
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {user.email}
              </span>
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                Sign Out
              </Button>
              <ModeToggle />
            </div>
          </header>

          <main className="flex-1 p-4 md:p-8 relative overflow-hidden">
            <img
              src={portalPhotoLeft}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="hidden xl:block pointer-events-none select-none absolute left-0 top-0 w-40 h-full object-cover opacity-[0.14] rounded-r-2xl"
            />
            <img
              src={portalPhotoRight}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="hidden xl:block pointer-events-none select-none absolute right-0 top-0 w-40 h-full object-cover opacity-[0.14] rounded-l-2xl"
            />
            <div className="relative z-10 xl:px-44 portal-content">{children}</div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
